// context/AuthContext.tsx
import { createContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { setSessionExpiredHandler } from "../services/api";
import api from "../services/api";

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "viewer";
  isZerodhaLinked: boolean;
}

export type AuthContextValue = {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  isLoading: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    // Register session-expired handler for axios
    setSessionExpiredHandler(() => logout());
    checkInitialAuth();
    return () => setSessionExpiredHandler(null);
  }, []);

  const checkAuthStatus = useCallback(async (): Promise<void> => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    try {
      const response = await api.get("/auth/user", {
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      if (response.status === 200) {
        setUser(response.data);
        setToken(storedToken);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("token");
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem("token");
    }
  }, []);

  const checkInitialAuth = useCallback(async () => {
    setIsLoading(true);
    await checkAuthStatus();
    setIsLoading(false);
  }, [checkAuthStatus]);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { token, user, requiresZerodha, zerodhaUrl } = response.data;
      
      localStorage.setItem("token", token);
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);

      // If admin, redirect to Zerodha for trading access
      if (requiresZerodha && zerodhaUrl) {
        console.log("🔐 Admin user - redirecting to Zerodha for trading access...");
        window.location.href = zerodhaUrl;
      }
      // Viewer users will be redirected in the Login component
    } catch (error: any) {
      console.error("Login error:", error);
      throw new Error(error.response?.data?.error || "Login failed");
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    try {
      const response = await api.post("/auth/signup", { email, password, name });
      const data = response.data;
      
      localStorage.setItem("token", data.token);
      setToken(data.token);
      setUser(data.user);
      setIsAuthenticated(true);
    } catch (error: any) {
      console.error("Signup error:", error);
      throw new Error(error.response?.data?.error || "Signup failed");
    }
  };

  function logout() {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login");
  }

  return (
    <AuthContext.Provider
      value={{ 
        isAuthenticated, 
        user, 
        token, 
        isAdmin, 
        login, 
        signup, 
        logout, 
        checkAuthStatus, 
        isLoading 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
